import { syncedGet, syncedSet } from './serverStore';

const PROFILE_KEY = 'sl_client_profiles';

const clean = value => String(value || '').trim();
const companyKey = value => clean(value).toLowerCase();

export function normalizeContacts(contacts = []) {
  const normalized = [];
  (Array.isArray(contacts) ? contacts : []).forEach(contact => {
    const next = {
      name:clean(contact?.name || contact?.clientName || contact?.attention || contact?.contact),
      email:clean(contact?.email || contact?.clientEmail || contact?.contactEmail),
    };
    if (!next.name && !next.email) return;
    const nameKey = next.name.toLowerCase();
    const emailKey = next.email.toLowerCase();
    const existing = normalized.find(item =>
      (emailKey && item.email.toLowerCase() === emailKey) ||
      (nameKey && item.name.toLowerCase() === nameKey)
    );
    if (existing) {
      if (!existing.name && next.name) existing.name = next.name;
      if (!existing.email && next.email) existing.email = next.email;
    } else {
      normalized.push(next);
    }
  });
  return normalized;
}

export function getClientProfiles() {
  const profiles = syncedGet(PROFILE_KEY, []);
  if (Array.isArray(profiles)) return profiles;
  // Accept the object-map shape used by early contact builds without
  // rewriting it merely because the screen was opened.
  if (profiles && typeof profiles === 'object') {
    return Object.entries(profiles).map(([company, profile]) => ({
      ...(profile && typeof profile === 'object' ? profile : {}),
      company:clean(profile?.company || company),
    }));
  }
  return [];
}

export function getClientProfile(company) {
  const key = companyKey(company);
  return getClientProfiles().find(profile => companyKey(profile.company) === key) || null;
}

export function getCompanyContacts(company, sample = null) {
  // Source is the company. Older records sometimes stored the contact person
  // in `client`, so prefer the sample's source when one is available.
  const companyName = clean(sample?.source || sample?.company || company);
  const profile = getClientProfile(companyName);
  const key = companyKey(companyName);
  const matchingSamples = (syncedGet('sl_samples', []) || []).filter(record =>
    [record?.source, record?.company, record?.client]
      .some(value => companyKey(value) === key)
  );
  return normalizeContacts([
    ...(sample?.contacts || []),
    ...(profile?.contacts || []),
    {
      name:profile?.clientName || profile?.attention || profile?.contact,
      email:profile?.clientEmail || profile?.email || profile?.contactEmail,
    },
    ...matchingSamples.flatMap(record => record?.contacts || []),
    ...matchingSamples.map(record => ({
      name:record?.attention || record?.contact,
      email:record?.clientEmail || record?.email,
    })),
    {
      name:sample?.attention || sample?.contact,
      email:sample?.clientEmail || sample?.email,
    },
  ]);
}

export function getBillingContact(company) {
  const profile = getClientProfile(company);
  const saved = normalizeContacts(profile?.billingContact ? [profile.billingContact] : []);
  if (saved.length) return saved[0];
  const key = companyKey(company);
  const invoice = (syncedGet('sl_invoices', []) || [])
    .filter(record => companyKey(record?.client) === key)
    .sort((a, b) => String(b?.num || '').localeCompare(String(a?.num || ''), undefined, { numeric:true }))
    .find(record => record?.clientEmail || record?.email || record?.attention || record?.contact);
  return invoice ? {
    name:clean(invoice.attention || invoice.contact),
    email:clean(invoice.clientEmail || invoice.email),
  } : null;
}

export async function saveClientProfile(company, changes = {}) {
  const companyName = clean(company);
  if (!companyName) throw new Error('Company name is required');
  const profiles = getClientProfiles();
  const index = profiles.findIndex(profile => companyKey(profile.company) === companyKey(companyName));
  const previous = index >= 0 ? profiles[index] : {};
  const next = {
    ...previous,
    ...changes,
    company:companyName,
    contacts:normalizeContacts(changes.contacts !== undefined ? changes.contacts : previous.contacts),
    billingContact:changes.billingContact !== undefined
      ? (normalizeContacts([changes.billingContact])[0] || {name:'',email:''})
      : previous.billingContact,
    updatedAt:new Date().toISOString(),
  };
  const updated = [...profiles];
  if (index >= 0) updated[index] = next;
  else updated.push(next);
  updated.sort((a, b) => a.company.localeCompare(b.company));
  await syncedSet(PROFILE_KEY, updated);
  return next;
}

export const contactEmails = contacts => normalizeContacts(contacts)
  .map(contact => contact.email)
  .filter(Boolean)
  .join('; ');
